import { useRecoilState } from 'recoil'
import {
  ChangeEvent,
  DragEvent,
  ClipboardEvent,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react'
import { createStateFactory } from 'Store/create'
import { CreateDeleteIcon } from 'Icons'
import { PlusAddIcon } from 'Comps/svg/PlusAddIcon'
import Cropper, { Area, Point } from 'react-easy-crop'
import { getImageSizeFromDataURL } from 'Utils/img'
import { usePathname, useSearchParams } from 'next/navigation'
import clsx from 'clsx'
import { message } from 'antd'
import { MAX_USER_UPLOAD_IMG_SIZE } from '@/constant/img'
import { UPLOAD_IMG_CROP_RATIO } from '@/constant/limit'
import useI18n from 'Hooks/useI18n'
import { withLocale } from '@/locales'

type UploadCharImgProps = {
  disabled: boolean
  setCroppedArea: (value: Area | null) => void
  setCroppedAreaPixels: (value: Area | null) => void
  scene?: string
}

export const UploadCharImg = ({
  disabled,
  setCroppedArea,
  setCroppedAreaPixels,
  scene = 'character_image',
}: UploadCharImgProps) => {
  const { t, lang } = useI18n()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const ref = useRef<Cropper>(null)
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [isDragOver, setIsDragOver] = useState<boolean>(false)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isUpdateChar =
    pathname === withLocale('/update', lang) ||
    searchParams.get('update') === 'true'
  const [createCharForm, setCreateCharForm] = useRecoilState(
    createStateFactory(false, scene)
  )
  const [updateCharForm, setUpdateCharForm] = useRecoilState(
    createStateFactory(true, scene)
  )
  const charForm = isUpdateChar ? updateCharForm : createCharForm
  const setCharForm = isUpdateChar ? setUpdateCharForm : setCreateCharForm
  const {
    uploadImgReference: selectedFile,
    uploadImgReferencePreviewUrl: previewUrl,
    imgFaceReferenceCroppedAreaPixels,
  } = charForm

  const setImgReference = (file: File | null) => {
    setCharForm((prev) => ({ ...prev, uploadImgReference: file }))
  }
  const setImgReferencePreviewUrl = (url: string | null) => {
    setCharForm((prev) => ({
      ...prev,
      uploadImgReferencePreviewUrl: url,
    }))
  }
  const setImgReferencePath = useCallback(
    (path: string | null) => {
      setCharForm((prev) => ({
        ...prev,
        uploadImgReferencePath: path,
      }))
    },
    [setCharForm]
  )
  const setFaceReferenceCrop = useCallback(
    (croppedArea: Area | null, croppedAreaPixels: Area | null) => {
      setCharForm((prev) => ({
        ...prev,
        imgFaceReferenceCroppedArea: croppedArea,
        imgFaceReferenceCroppedAreaPixels: croppedAreaPixels,
      }))
    },
    [setCharForm]
  )

  // 解决 Cropper 组件是类组件导致关闭时crop会重置到原点的问题 TODO: 创建角色第一次裁剪没问题，第二次裁剪有问题，建议换一个库然后删掉useLayoutEffect里的逻辑
  useLayoutEffect(() => {
    // 解决第一次打开编辑器，放大图片然后关闭（不保存），再次打开编辑器时，zoom放大不恢复的问题
    setZoom(1)
    ref.current?.forceUpdate()
  }, [])

  const handleSaveOriginImg = async (userFile: File) => {
    if (userFile.size > MAX_USER_UPLOAD_IMG_SIZE) {
      message.error(
        t('The uploaded image exceeds the maximum allowed size of 20MB.')
      )
      return
    }
    setImgReference(userFile)
    setImgReferencePreviewUrl(URL.createObjectURL(userFile))
    setImgReferencePath(null)
    setFaceReferenceCrop(null, null)
    if (previewUrl === URL.createObjectURL(userFile)) return
    const { width: imgWidth, height: imgHeight } =
      await getImageSizeFromDataURL(URL.createObjectURL(userFile))
    const height = Math.min(
      Math.round(imgWidth * (1 / UPLOAD_IMG_CROP_RATIO)),
      imgHeight
    )
    const width = Math.round(height * UPLOAD_IMG_CROP_RATIO)
    const initialCropArea = {
      height,
      width,
      x: height === imgHeight ? Math.round((imgWidth - width) / 2) : 0,
      y: height === imgHeight ? 0 : Math.round((imgHeight - height) / 2),
    }
    setCroppedArea(initialCropArea)
    setCroppedAreaPixels(initialCropArea)
  }
  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files.length) return
    const userFile: File = e.target.files[0]
    handleSaveOriginImg(userFile)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleSaveOriginImg(e.dataTransfer.files[0])
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (e.clipboardData.files.length) {
      handleSaveOriginImg(e.clipboardData.files[0])
    }
  }

  const handleClick = () => {
    if (selectedFile) return
    fileInputRef.current?.click()
  }

  const handleCancel = () => {
    setImgReference(null)
    setImgReferencePreviewUrl(null)
    setImgReferencePath(null)
    setFaceReferenceCrop(null, null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedArea)
    setCroppedAreaPixels(croppedAreaPixels)
  }
  if (!selectedFile)
    return (
      <div
        onDrop={handleDrop}
        onPaste={handlePaste}
        onClick={handleClick}
        className={clsx(
          'flex h-full w-full cursor-pointer flex-col items-center justify-center p-2 text-center text-sm text-[#756A6E]'
        )}
      >
        <input
          type="file"
          accept="image/*"
          name="file"
          onChange={handleImageChange}
          ref={fileInputRef}
          className="hidden"
          disabled={disabled}
        />
        <PlusAddIcon size={36} />

        <div className="px-1 pt-4">
          {t('Drag and drop or click to upload image(optional)')}
        </div>

        <div
          className={clsx(
            'absolute inset-0 h-full w-full rounded-lg border border-transparent',
            {
              '!border-[#FFA133]': isDragOver,
            }
          )}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
        ></div>
      </div>
    )

  return (
    <div
      className={clsx(
        'relative flex h-full w-full items-center justify-center'
      )}
    >
      {previewUrl && (
        <div className="relative h-full w-full">
          <Cropper
            ref={ref}
            image={previewUrl}
            crop={crop}
            zoom={zoom}
            maxZoom={5}
            aspect={UPLOAD_IMG_CROP_RATIO}
            style={{
              mediaStyle: { maxWidth: 'none' },
              containerStyle: { borderRadius: '8px' },
            }}
            objectFit="vertical-cover"
            cropShape="rect"
            showGrid={false}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onCropAreaChange={setCroppedArea}
            onZoomChange={setZoom}
            initialCroppedAreaPixels={undefined}
          />
        </div>
      )}
      <div className="absolute right-[5px] top-[5px] cursor-pointer">
        {!disabled && <CreateDeleteIcon onClick={handleCancel} />}
      </div>
    </div>
  )
}

export const UploadCharImgOutput = ({
  url,
  croppedArea,
}: {
  url: string
  croppedArea: Area
}) => {
  const { t } = useI18n()
  const scale = 100 / croppedArea.width
  const transform = {
    x: `${-croppedArea.x * scale}%`,
    y: `${-croppedArea.y * scale}%`,
    scale,
    width: 'calc(100% + 0.5px)',
    height: 'auto',
  }

  const imageStyle = {
    transform: `translate3d(${transform.x}, ${transform.y}, 0) scale3d(${transform.scale},${transform.scale},1)`,
    width: transform.width,
    height: transform.height,
    transformOrigin: 'top left',
  }

  return <img src={url} alt={t('Preview')} style={imageStyle} />
}
